import { useState } from "react";
import Picker from "react-mobile-picker";
import moment from "moment";
import useClickOutside from "./../../../app/utils/useClickOutside";
import TimeIcon from "../../home/icons/TimeIcon";

interface PickerValueObj {
  [key: string]: string;
}

function renderOptions(options: string[], selectedColor: string) {
  return options.map((option) => (
    <Picker.Item key={option} value={option}>
      {({ selected }: { selected: boolean }) => (
        <div
          className={
            selected
              ? `font-semibold cursor-pointer text-[16px] ${selectedColor}`
              : "text-neutral-400 text-[14px]"
          }
        >
          {option}
        </div>
      )}
    </Picker.Item>
  ));
}

type TCustomTimePicker = {
  label?: string;
  name?: string;
  setTime: (time: string) => void;
  timeValue: string;
  color?: string;
  disabled?: boolean;
  height?: string;
  placeHolder?: string;
  dropDownClassName?: string;
  /** Force the dropdown to open upward */
  showPickerToTop?: boolean;
};

const CustomTimePicker = ({
  label,
  setTime,
  timeValue,
  height = "45px",
  disabled = false,
  color,
  placeHolder,
  dropDownClassName,
  showPickerToTop = false,
}: TCustomTimePicker) => {
  const hours = Array.from({ length: 12 }, (_, i) =>
    String(i + 1).padStart(2, "0"),
  );
  const minutes = Array.from({ length: 60 }, (_, i) =>
    String(i).padStart(2, "0"),
  );
  const periods = ["AM", "PM"];

  const [showTimePicker, setShowTimePicker] = useState(false);
  const [pickerValue, setPickerValue] = useState<PickerValueObj>({
    hour: "",
    minute: "",
    period: "",
  });

  const handleValueChange = (newValue: PickerValueObj) => {
    setPickerValue(newValue);
  };

  const handleSelectTime = () => {
    const time = `${pickerValue.hour}:${pickerValue.minute} ${pickerValue.period}`;
    setTime(moment(time, "hh:mm A").format("HH:mm"));
    setShowTimePicker(false);
  };

  const togglePicker = () => {
    if (disabled) return;
    setShowTimePicker((prev) => !prev);
  };

  function convertTo12Hour(time: string): string {
    if (!time) return "";
    const [h, m] = time.split(":").map(Number);
    const period = h >= 12 ? "PM" : "AM";
    const hours = h % 12 || 12;
    return `${hours}:${String(m).padStart(2, "0")} ${period}`;
  }

  const isDisabled =
    !pickerValue.hour || !pickerValue.minute || !pickerValue.period;

  const ref = useClickOutside(() => setShowTimePicker(false));

  return (
    <div ref={ref} className="relative w-full">
      {/* Trigger */}
      <div className="relative">
        {label && (
          <label className="text-[#6F6464] text-[12px] font-semibold block mb-1">
            {label}
          </label>
        )}
        <input
          style={{ lineHeight: "16px", height }}
          onClick={togglePicker}
          placeholder={placeHolder || "Select Time"}
          readOnly
          type="text"
          value={timeValue ? convertTo12Hour(timeValue) : ""}
          disabled={disabled}
          className={`${
            color ? color : "bg-white"
          } rounded-md border border-[#DFDFDF] ${
            disabled ? "cursor-not-allowed opacity-40" : "cursor-pointer"
          } ${
            timeValue ? "text-[#303030]" : "text-[#999]"
          } font-normal text-[12px] w-full focus:outline-none px-3 pr-9`}
        />
        <div className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
          <TimeIcon />
        </div>
      </div>

      {/* Dropdown – always centered under the input */}
      {showTimePicker && (
        <div
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          style={{ boxShadow: "0px 4px 12px 0px rgba(0, 0, 0, 0.14)" }}
          className={`
            absolute z-50 left-5 -translate-x-1/2
            w-[min(100%,280px)] min-w-60
            bg-white border border-[#DFDFDF] rounded-md
            overflow-hidden
            ${showPickerToTop ? "bottom-[calc(100%+6px)]" : "top-[calc(100%+6px)]"}
            ${dropDownClassName || ""}
          `}
        >
          {/* Single Picker with 3 columns – this is the correct API */}
          <div className="flex items-center justify-center px-2 pt-3">
            <Picker
              value={pickerValue}
              onChange={handleValueChange}
              wheelMode="natural"
              className="w-full"
            >
              <Picker.Column name="hour" className="flex-1">
                {renderOptions(hours, "text-[#2D65F2]")}
              </Picker.Column>

              <div className="text-[16px] text-[#2D65F2] font-medium px-1 self-center">
                :
              </div>

              <Picker.Column name="minute" className="flex-1">
                {renderOptions(minutes, "text-[#2D65F2]")}
              </Picker.Column>

              <Picker.Column name="period" className="flex-1">
                {renderOptions(periods, "text-[#2D65F2]")}
              </Picker.Column>
            </Picker>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 px-4 py-3 border-t border-[#F0F0F0]">
            <button
              type="button"
              onClick={togglePicker}
              className="flex-1 py-1.5 text-[12px] font-semibold text-[#999] cursor-pointer"
            >
              Close
            </button>
            <button
              type="button"
              disabled={isDisabled}
              onClick={handleSelectTime}
              className={`flex-1 py-1.5 rounded-md bg-[#2D65F2] text-white text-[12px] font-semibold ${
                isDisabled
                  ? "opacity-40 cursor-not-allowed"
                  : "cursor-pointer hover:bg-[#1e4fd1]"
              }`}
            >
              Select
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomTimePicker;
